import {CircleAlert, ImageOff, LoaderCircle, Plus, X} from 'lucide-react';
import {EmbeddedBlock, ReadBuyer} from '@tinct/core-api-models';
import {useDeleteBuyer, useGetBuyers} from '@tinct/core-api';
import {
  BUYER_IMPORT_JOB_TAG,
  getBuyerFillLogoUrl,
  getBuyerLogoUrl,
  ImportBuyerModal,
  UploadBuyerLogoModal,
} from '@tinct/web-feature-companies';
import {useJobStore} from '@tinct/web-layout';
import {Button, LogoImage, Skeleton} from '@tinct/web-components';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {SidebarGroupLabel} from './shared';
import {cn} from "@tinct/core-utils";
import {isLightHexColor} from '../../../../utils/is-light-hex-color';

type SocialProofLogosSectionProps = {
  block: EmbeddedBlock;
  organizationId?: string;
  /** Called after a buyer is successfully removed from the library, with the
   *  buyer as it was — the caller needs its company to re-match the logos. */
  onBuyerRemoved?: (buyer: ReadBuyer) => void;
};

const LogoCard = ({
  greyscale = false,
  dark = false,
  fillColor = null,
  buyer,
  onRemove,
  onUpload,
}: {
  greyscale?: boolean;
  dark?: boolean;
  fillColor?: string | null;
  buyer: ReadBuyer;
  onRemove?: (buyerId: string) => void;
  onUpload?: (buyer: ReadBuyer) => void;
}) => {
  // A fill color repaints every pixel, so any base variant works; otherwise
  // the greyscale/dark combination must resolve exactly.
  const logoUrl = fillColor
    ? getBuyerFillLogoUrl(buyer)
    : getBuyerLogoUrl(buyer, { greyscale, dark });
  const isMissing = !logoUrl;
  const name = buyer.company?.name ?? '';
  const buyerId = buyer.id;

  return (
    <div
      role={onUpload ? 'button' : undefined}
      tabIndex={onUpload ? 0 : undefined}
      aria-label={
        onUpload
          ? `${isMissing ? 'Add' : 'Replace'} logo for ${name || 'customer'}`
          : undefined
      }
      onClick={onUpload ? () => onUpload(buyer) : undefined}
      onKeyDown={
        onUpload
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onUpload(buyer);
              }
            }
          : undefined
      }
      className={cn(
        'group relative flex aspect-square flex-col rounded-md border border-border px-2 pt-2 pb-1 transition-colors bg-background hover:bg-neutral-50',
        {
          // Light fill colors are invisible on the light card — flip it to dark.
          'dark': !!fillColor && isLightHexColor(fillColor) && !isMissing,
          'bg-muted': isMissing,
          'cursor-pointer': onUpload,
        },
      )}
    >
      {/* Remove (revealed on hover) */}
      {buyerId && onRemove && (
        <button
          type="button"
          aria-label={`Remove ${name || 'customer'}`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(buyerId);
          }}
          className="absolute right-2 top-2 hidden opacity-70 transition-opacity hover:opacity-100 group-hover:block"
        >
          <X className="size-4 text-foreground" />
        </button>
      )}
      <div className="flex min-h-0 flex-1 items-center justify-center px-2 pt-2">
        {logoUrl ? (
          <LogoImage
            src={logoUrl}
            alt={name}
            fillColor={fillColor}
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <ImageOff className="size-6 text-muted-foreground" />
        )}
      </div>
      <p
        className={cn(
          'w-full truncate text-center text-xs font-medium leading-[18px]',
          isMissing
            ? 'text-destructive'
            : 'text-muted-foreground group-hover:text-foreground'
        )}
      >
        {name}
      </p>
    </div>
  );
};

/**
 * Customer-logo pool for `customer_logo` blocks: the logo grid (or an
 * empty state) plus a relevance hint. Logos are the organization's buyers;
 * adding one opens the import modal. The color/greyscale/dark toggles live in
 * the Content card (see DisplayLogosInColorToggle). Buyers whose logo can't be
 * resolved for the current toggle combination show as "missing" cards, with an
 * All/Missing filter and an alert to triage them.
 */
export const SocialProofLogosSection = ({
  block,
  organizationId,
  onBuyerRemoved,
}: SocialProofLogosSectionProps) => {
  const [isImportOpen, setIsImportOpen] = useState(false);
  // Return the user to the current editor once a background import completes.
  const { pathname } = useLocation();
  const openImport = useCallback(() => setIsImportOpen(true), []);

  // While a buyer-import job is running, poll so newly imported logos appear.
  const isImportingBuyers = useJobStore((s) =>
    s.jobs.some((job) => job.tag === BUYER_IMPORT_JOB_TAG && job.status === 'running')
  );

  const { data, isLoading, refetch } = useGetBuyers(
    {
      offset: 0,
      limit: 100,
      filters: { organizationId },
      fields: {
        id: null,
        brandmarkLogoGreyscale: null,
        brandmarkLogo: null,
        brandmarkLogoDark: null,
        brandmarkLogoGreyscaleDark: null,
        company: {
         fields: {
           id: null,
           name: null,
           brandmarkLogoGreyscale: null,
           brandmarkLogo: null,
           brandmarkLogoDark: null,
           brandmarkLogoGreyscaleDark: null,
         }
        }
      }
    },
    { refetchInterval: isImportingBuyers ? 5000 : false }
  );

  // Polling stops the instant the import finishes, so the last poll can be up
  // to the interval stale. Fetch once more when isImportingBuyers goes
  // true -> false to capture the final buyer list.
  const wasImportingBuyersRef = useRef(isImportingBuyers);
  useEffect(() => {
    if (wasImportingBuyersRef.current && !isImportingBuyers) {
      void refetch();
    }
    wasImportingBuyersRef.current = isImportingBuyers;
  }, [isImportingBuyers, refetch]);
  const buyers = useMemo(() => data?.data ?? [], [data?.data]);
  const hasBuyers = buyers.length > 0;

  // Buyers whose logo can't be resolved for the block's current rendering
  // settings. They render as "missing" cards and are excluded from the banner,
  // so the user can filter and fix them. With a fill color any base variant
  // works (the fill repaints it); otherwise the greyscale/dark combination
  // must resolve exactly.
  const greyscale = block.greyscale ?? false;
  const dark = block.dark ?? false;
  const fillColor = block.logoFillColor ?? null;
  const missingBuyers = useMemo(
    () =>
      buyers.filter((buyer) =>
        fillColor
          ? !getBuyerFillLogoUrl(buyer)
          : !getBuyerLogoUrl(buyer, { greyscale, dark })
      ),
    [buyers, greyscale, dark, fillColor]
  );
  const missingCount = missingBuyers.length;

  const [logoFilter, setLogoFilter] = useState<'all' | 'missing'>('all');
  // Fall back to "all" when nothing is missing (e.g. the toggles changed).
  const activeFilter = missingCount > 0 ? logoFilter : 'all';
  const visibleBuyers = activeFilter === 'missing' ? missingBuyers : buyers;

  // Removing a buyer makes the backend drop its logo from every page showing
  // it and re-match the affected campaigns; the studio then tracks the job of
  // the campaign it is on (see useLogoRematch), which is why `onBuyerRemoved`
  // receives the buyer — its company is what that lookup needs. Passing
  // `onSuccess` here adds to the hook's own callback (cache invalidation), it
  // doesn't replace it.
  const { mutate: deleteBuyer } = useDeleteBuyer();
  const handleRemove = useCallback(
    (buyerId: string) => {
      const buyer = buyers.find((b) => b.id === buyerId);
      deleteBuyer({ buyerId }, { onSuccess: () => buyer && onBuyerRemoved?.(buyer) });
    },
    [buyers, deleteBuyer, onBuyerRemoved]
  );

  // Clicking a logo card opens the upload modal for that buyer (add when the
  // current variant is missing, replace otherwise).
  const [uploadBuyer, setUploadBuyer] = useState<ReadBuyer | null>(null);

  return (
    <div className="flex flex-col gap-4">
      {/* Logo pool */}
      <div className="flex flex-col">
        <SidebarGroupLabel label="Your customers library" />
        {/* No overflow-hidden here: it would become the scrollport for the
            sticky import pill below and stop it from sticking. */}
        <div className={cn("relative flex flex-col gap-4", {
          'rounded-2xl bg-secondary p-4': !hasBuyers
        })}>
          {/* Import in progress: block the section until the job finishes and
              the polled buyer list catches up. */}
          {isImportingBuyers && (
            <div className={cn('absolute inset-0 z-10 bg-background/70', {
              'rounded-2xl': !hasBuyers
            })}>
              {/* Sticky so the label stays visible while the (tall) section
                  scrolls in the sidebar. */}
              <div className="sticky top-4 flex justify-center py-20">
                <div className="flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 shadow-md">
                  <LoaderCircle className="size-4 animate-spin text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">
                    Importing customers...
                  </span>
                </div>
              </div>
            </div>
          )}
          {/* Missing-logo triage: filter tabs + alert, only when some logos
              can't be resolved for the current greyscale/dark combination. */}
          {hasBuyers && missingCount > 0 && (
            <>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setLogoFilter('all')}
                  className={cn(
                    'flex h-9 items-center justify-center gap-2 rounded-md px-3 text-sm font-medium transition-colors',
                    activeFilter === 'all'
                      ? 'border-[1.5px] border-foreground bg-background text-foreground shadow-xs'
                      : 'border border-input text-muted-foreground hover:bg-accent'
                  )}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => setLogoFilter('missing')}
                  className={cn(
                    'flex h-9 items-center justify-center gap-2 rounded-md px-3 text-sm font-medium transition-colors',
                    activeFilter === 'missing'
                      ? 'border-[1.5px] border-foreground bg-background text-foreground shadow-xs'
                      : 'border border-input text-muted-foreground hover:bg-accent'
                  )}
                >
                  <CircleAlert className="size-4" />
                  Missing ({missingCount})
                </button>
              </div>
              <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4">
                <p className="text-sm leading-5 text-destructive">
                  Tinct could not find some logos, but you can add them.
                  Missing logos will not be used on your logo banner.
                </p>
              </div>
            </>
          )}

          {isLoading ? (
            <div className="grid grid-cols-3 gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square rounded-md" />
              ))}
            </div>
          ) : hasBuyers ? (
            <div className={cn("grid grid-cols-3 gap-2", {
              'dark': block.dark ?? false
            })}>
              {activeFilter === 'all' && (
                <button
                  type="button"
                  onClick={openImport}
                  aria-label="Add customer"
                  className="flex aspect-square items-center justify-center rounded-md border border-input bg-background shadow-xs transition-colors hover:bg-accent"
                >
                  <Plus className="size-4 text-foreground" />
                </button>
              )}
              {visibleBuyers.map((buyer) => (
                <LogoCard
                  key={buyer.id}
                  buyer={buyer}
                  dark={dark}
                  greyscale={greyscale}
                  fillColor={fillColor}
                  onRemove={handleRemove}
                  onUpload={setUploadBuyer}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-foreground">
                  No customer logos yet
                </p>
                <p className="text-sm leading-5 text-muted-foreground">
                  Add your customers so Tinct can pick the most relevant for
                  each visitor.
                </p>
              </div>
              <Button variant="outline" onClick={openImport}>
                Add customer
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Relevance hint */}
      <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-4">
        <p className="text-sm leading-5 text-indigo-600">
          The relevance of the custom logo banners will depend directly on the
          size of your logo pool.
        </p>
      </div>

      <ImportBuyerModal
        open={isImportOpen}
        onOpenChange={setIsImportOpen}
        completedUrl={pathname}
      />

      <UploadBuyerLogoModal
        open={!!uploadBuyer}
        onOpenChange={(isOpen) => {
          if (!isOpen) setUploadBuyer(null);
        }}
        buyer={uploadBuyer}
        greyscale={greyscale}
        dark={dark}
      />
    </div>
  );
};
